import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ListRemindersScreen.module.scss';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import RemindersService from 'api/reminders/RemindersService';
import { ReminderListItemDto } from 'api/reminders/models/ReminderListItemDto';
import { RemindersSearchCriteria } from 'api/reminders/models/RemindersSearchCriteria';
import ReminderListTable from './components/reminderListTable/ReminderListTable';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import { useParams } from 'react-router-dom';
import { Filters } from './components/ReminderFiltersScreen';

export type Props = {
    criteria?: RemindersSearchCriteria;
    editable: boolean;
    onClickItem: (item: ReminderListItemDto, event: any) => void;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const ListRemindersScreen = ({ criteria: _criteria, editable, onClickItem, exportRef }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { vehicleId } = useParams<{ vehicleId: string }>();

    const FILTERS_CACHE_KEY = 'REMINDERSFILTER';

    const [reminders, setReminders] = useState<ReminderListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const [criteria, setCriteria] = useState<RemindersSearchCriteria>(_criteria || {
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'r.created_date',
        orderBy: 'desc',
        vehicleId: vehicleId,
        vehiclesInactive: true,
    });

    const [filters, setFilters] = useState<Filters>({});
    const isFirstLoad = useRef<boolean>(true);
    const previousCriteria = useRef<RemindersSearchCriteria>(criteria);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);
    const getDataVersion = useRef(0);

    const getData = async (isLoadMore = false) => {
        const currentVersion = ++getDataVersion.current;
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const crit = { ...criteria, vehicleId: vehicleId ?? criteria.vehicleId };

            saveOrdernationFilters();

            const res = await RemindersService.getList(crit);

            if (currentVersion !== getDataVersion.current) return;

            setTotalItems(res.totalItems);

            if (isLoadMore) {
                setReminders(prev => {
                    const existingIds = new Set(prev.map(r => r.triggerId));
                    return [...prev, ...res.items.filter(r => !existingIds.has(r.triggerId))];
                });
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setReminders(res.items);
            }

            Loading.hide();
            setIsLoadingMore(false);

            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(res.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...crit, page: currentPage - 1 };
                        const prevResult = await RemindersService.getList(prevCriteria);
                        if (currentVersion !== getDataVersion.current) return;
                        setReminders(prev => {
                            const existingIds = new Set(prev.map(r => r.triggerId));
                            return [...prevResult.items.filter(r => !existingIds.has(r.triggerId)), ...prev];
                        });
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...crit, page: currentPage + 1 };
                        const nextResult = await RemindersService.getList(nextCriteria);
                        if (currentVersion !== getDataVersion.current) return;
                        setReminders(prev => {
                            const existingIds = new Set(prev.map(r => r.triggerId));
                            return [...prev, ...nextResult.items.filter(r => !existingIds.has(r.triggerId))];
                        });
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            if (currentVersion !== getDataVersion.current) return;
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't get reminders list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        }
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        getDataVersion.current++;
        setReminders([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field }));
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || reminders.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, reminders.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();

            const prevCriteria: RemindersSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await RemindersService.getList(prevCriteria);

            setReminders(prev => {
                const existingIds = new Set(prev.map(r => r.triggerId));
                return [...result.items.filter(r => !existingIds.has(r.triggerId)), ...prev];
            });
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't get previous reminders page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    const fetchAllForExport = useCallback(async () => {
        const result = await RemindersService.getList({
            ...criteria,
            vehicleId: vehicleId ?? criteria.vehicleId,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria, vehicleId]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [reminders, isLoadingPrevious, adjustScrollAfterPrepend]);

    useEffect(() => {
        if (_criteria && !isFirstLoad.current) {
            onChangeFiltersFromParent(_criteria);
        }
    }, [_criteria]);

    useEffect(() => {
        if (isFirstLoad.current) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && reminders.length > 0;
            getData(isLoadMore);
            previousCriteria.current = criteria;
        }
    }, [criteria]);

    const onChangeFiltersFromParent = (parentCriteria: RemindersSearchCriteria) => {
        getDataVersion.current++;
        setReminders([]);
        resetScrollState();
        setTotalItems(0);

        // Sync local filters from cache (parent already saved them to localStorage)
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters) {
            setFilters(JSON.parse(cachedFilters));
        }

        setCriteria(prev => ({
            ...prev,
            ...parentCriteria,
            page: 1,
        }));
        setReRenderSearch(r => r + 1);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    vehicleId: f.vehicleId,
                    categoryId: f.categoryId,
                    localIds: f.localIds,
                    localOperator: f.localOperator,
                    segmentId: f.segmentId,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));
                isFirstLoad.current = false;
                return;
            }

            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));

            if (!_criteria) {
                setFilters(f);
                setCriteria(prev => ({
                    ...prev,
                    vehicleId: f.vehicleId,
                    categoryId: f.categoryId,
                    localIds: f.localIds,
                    localOperator: f.localOperator,
                    segmentId: f.segmentId,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: 1,
                }));
            } else {
                setFilters(f);
                setCriteria(prev => ({
                    ...prev,
                    ..._criteria,
                    vehicleId: f.vehicleId,
                    categoryId: f.categoryId,
                    localIds: f.localIds,
                    localOperator: f.localOperator,
                    segmentId: f.segmentId,
                    orderBy: f.orderBy || _criteria?.orderBy,
                    orderColumn: f.orderColumn || _criteria?.orderColumn,
                    page: 1,
                }));
            }
        } else {
            getData(false);
        }

        isFirstLoad.current = false;
    };

    const onRowClick = (item: ReminderListItemDto, event: any) => {
        const itemIndex = reminders.findIndex(r => r.triggerId === item.triggerId);
        const actualPage = calculatePageFromIndex(itemIndex);

        const newFilters = { ...filters };
        newFilters.page = actualPage;
        (newFilters as any).scrollToItemId = item.triggerId;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);

        onClickItem(item, event);
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        if ((f as any).scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page: _page, ...filtersWithoutPage } = f;
            const { scrollToItemId: _scrollToItemId, ...rest } = filtersWithoutPage as any;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(rest));
        }
    };

    const onDeleteFromList = () => {
        getDataVersion.current++;
        setReminders([]);
        resetScrollState();
        setCriteria(prev => ({ ...prev, page: 1 }));
    };

    return (
        <div className={styles.listWrapper}>
            {reminders.length > 0 || totalItems === 0 ? (
                <div className={styles.listTblWrapper}>
                    <ReminderListTable
                        reRenderSearch={reRenderSearch}
                        items={reminders}
                        totalItems={totalItems}
                        isLoadingMore={isLoadingMore}
                        hasMore={getHasMore(reminders.length, totalItems)}
                        editable={editable}
                        onLoadMore={getOnLoadMore(loadMore)}
                        onTableFilter={onTableFilter}
                        onClickItem={onRowClick}
                        onDeleteFromList={onDeleteFromList}
                        criteria={criteria}
                        scrollToRowId={scrollToItemId}
                        hasMorePrevious={getHasMorePrevious()}
                        isLoadingPrevious={isLoadingPrevious}
                        onLoadPrevious={getOnLoadPrevious(loadPrevious)}
                        onFetchAllForExport={fetchAllForExport}
                        exportRef={exportRef}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default ListRemindersScreen;
